import React, { useState } from 'react';
import { useAuth } from '../../services/authService';
import { useProject } from '../../context/ProjectContext';
import { Bell, Search, User, LogOut, Settings as SettingsIcon, ChevronDown, FolderOpen } from 'lucide-react';

const Header = () => {
  const { user, logout, tenant } = useAuth();
  const { project, projects, switchProject } = useProject();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showProjectMenu, setShowProjectMenu] = useState(false);

  const handleLogout = async () => {
    await logout();
  };

  return (
    <div className="sticky top-0 z-40 flex-shrink-0 flex h-14 bg-white border-b border-slate-200">
      <div className="flex-1 px-6 flex items-center justify-between">
        {/* Logo (visible on mobile when sidebar is hidden) */}
        <div className="flex items-center lg:hidden mr-4">
          <div className="w-8 h-8 bg-blue-600 rounded-md flex items-center justify-center">
            <img
              src="/logo.png"
              alt="Aahwahan Foundation"
              className="w-full h-full object-contain filter brightness-0 invert"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <User className="w-4 h-4 text-white hidden" />
          </div>
        </div>

        {/* Project Switcher */}
        <div className="relative mr-6">
          <button
            onClick={() => setShowProjectMenu(!showProjectMenu)}
            className="flex items-center gap-2 px-3 py-2 text-slate-600 font-medium text-sm hover:text-slate-900 transition-colors"
          >
            <FolderOpen className="w-4 h-4 text-slate-400" />
            <span className="max-w-[160px] truncate">{project?.name || 'Select Project'}</span>
            <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${showProjectMenu ? 'rotate-180' : ''}`} />
          </button>
          {showProjectMenu && projects.length > 1 && (
            <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg border border-slate-200 shadow-sm py-1 z-50">
              {projects.map((p) => (
                <button key={p.id} onClick={() => { switchProject(p.id); setShowProjectMenu(false); }}
                  className={`w-full text-left px-4 py-2.5 text-sm hover:bg-slate-100 transition-colors ${p.id === project?.id ? 'bg-blue-50 text-blue-600 font-medium' : 'text-slate-700'}`}>
                  <div className="font-medium">{p.name}</div>
                  <div className="text-xs text-slate-400">/{p.slug}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Search */}
        <div className="flex-1 flex max-w-lg">
          <div className="w-full">
            <label htmlFor="search-field" className="sr-only">
              Search
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Search className="h-4 w-4 text-slate-400" />
              </div>
              <input
                id="search-field"
                className="block w-full pl-10 pr-3 py-2 bg-slate-100 border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-colors text-sm"
                placeholder="Search..."
                type="search"
              />
            </div>
          </div>
        </div>

        {/* Right side */}
        <div className="ml-6 flex items-center gap-4">
          {/* Notifications */}
          <button className="relative p-2 text-slate-600 hover:text-slate-900 transition-colors">
            <span className="sr-only">View notifications</span>
            <Bell className="h-5 w-5" />
            <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-orange-500 rounded-full" />
          </button>

          {/* Profile dropdown */}
          <div className="relative">
            <button
              className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-100 rounded-lg transition-colors"
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              <div className="w-8 h-8 bg-blue-600 rounded-md flex items-center justify-center flex-shrink-0">
                <User className="h-4 w-4 text-white" />
              </div>
              <div className="hidden md:block text-left">
                <div className="text-sm font-medium text-slate-900">
                  {user?.fullName || user?.email}
                </div>
              </div>
              <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
            </button>

            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg border border-slate-200 shadow-sm py-1 z-50">
                <div className="px-4 py-3 border-b border-slate-200">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-blue-600 rounded-md flex items-center justify-center flex-shrink-0">
                      <User className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-900">
                        {user?.fullName || user?.email}
                      </div>
                      <div className="text-xs text-slate-500">{user?.email}</div>
                    </div>
                  </div>
                </div>

                <div className="py-1">
                  <button className="flex items-center w-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 transition-colors">
                    <User className="h-4 w-4 text-slate-400 mr-3" />
                    <div>
                      <div className="font-medium">Profile Settings</div>
                      <div className="text-xs text-slate-400">Manage your account</div>
                    </div>
                  </button>

                  <button className="flex items-center w-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 transition-colors">
                    <SettingsIcon className="h-4 w-4 text-slate-400 mr-3" />
                    <div>
                      <div className="font-medium">System Settings</div>
                      <div className="text-xs text-slate-400">Configure preferences</div>
                    </div>
                  </button>
                </div>

                <div className="border-t border-slate-200 py-1">
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 transition-colors"
                  >
                    <LogOut className="h-4 w-4 text-slate-400 mr-3" />
                    <div>
                      <div className="font-medium">Sign Out</div>
                      <div className="text-xs text-slate-400">End your session</div>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;